import { useState } from 'react';
import { X, AlertTriangle, TrendingDown, Clock, DollarSign, Split } from 'lucide-react';

interface CreateScenarioModalProps {
  onClose: () => void;
  onCreateScenario: (
    name: string,
    description: string,
    impactType: string,
    impactData: any
  ) => void;
  treasuryLines: Array<{
    id: string;
    date: Date;
    label: string;
    amount: number;
  }>;
}

type ImpactType = 'DELAY' | 'LOSS' | 'PARTIAL' | 'UNEXPECTED' | 'OVERCOST' | 'SPREAD';

export function CreateScenarioModal({ onClose, onCreateScenario, treasuryLines }: CreateScenarioModalProps) {
  const [step, setStep] = useState(1);
  const [scenarioName, setScenarioName] = useState('');
  const [scenarioDescription, setScenarioDescription] = useState('');
  const [impactType, setImpactType] = useState<ImpactType>('DELAY');
  const [selectedLineId, setSelectedLineId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [delayDays, setDelayDays] = useState(30);
  const [percentage, setPercentage] = useState(0);
  const [amount, setAmount] = useState(0);
  const [spreadPeriods, setSpreadPeriods] = useState(3);

  const impactTypes = [
    { id: 'DELAY' as ImpactType, label: 'Retard de paiement', icon: Clock, description: 'Décaler une ligne existante' },
    { id: 'LOSS' as ImpactType, label: 'Perte totale', icon: TrendingDown, description: 'Annuler complètement un encaissement' },
    { id: 'PARTIAL' as ImpactType, label: 'Encaissement partiel', icon: DollarSign, description: 'Réduire un montant attendu' },
    { id: 'UNEXPECTED' as ImpactType, label: 'Décaissement imprévu', icon: AlertTriangle, description: 'Ajouter une dépense non prévue' },
    { id: 'OVERCOST' as ImpactType, label: 'Surcoût', icon: TrendingDown, description: 'Augmenter une dépense existante' },
    { id: 'SPREAD' as ImpactType, label: 'Étirement de paiement', icon: Split, description: 'Répartir un montant sur plusieurs mois' }
  ];

  const filteredLines = treasuryLines.filter(line =>
    line.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    line.amount.toString().includes(searchTerm)
  );

  const selectedLine = treasuryLines.find(l => l.id === selectedLineId);

  const handleNext = () => {
    if (step === 1 && impactType) {
      setStep(2);
    } else if (step === 2 && selectedLineId && (impactType === 'DELAY' || impactType === 'PARTIAL' || impactType === 'LOSS' || impactType === 'OVERCOST')) {
      setStep(3);
    } else if (step === 2 && (impactType === 'UNEXPECTED' || impactType === 'SPREAD')) {
      setStep(3);
    }
  };

  const handleCreate = () => {
    let impactData: any = {
      type: impactType,
      target_line_id: selectedLineId || undefined
    };

    switch (impactType) {
      case 'DELAY':
        impactData.delay_days = delayDays;
        break;
      case 'LOSS':
        impactData.percentage = 0;
        break;
      case 'PARTIAL':
        impactData.percentage = percentage / 100;
        break;
      case 'UNEXPECTED':
        impactData.amount = -Math.abs(amount);
        break;
      case 'OVERCOST':
        impactData.percentage = 1 + (Math.abs(percentage) / 100);
        break;
      case 'SPREAD':
        impactData.amount = amount;
        impactData.spread_periods = spreadPeriods;
        break;
    }

    onCreateScenario(scenarioName, scenarioDescription, impactType, impactData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Créer un Crash Test</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              {[1, 2, 3].map(s => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {s}
                  </div>
                  {s < 3 && (
                    <div className={`w-24 h-1 mx-2 ${
                      step > s ? 'bg-blue-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Étape 1 : Type de problème</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du scénario
                </label>
                <input
                  type="text"
                  value={scenarioName}
                  onChange={(e) => setScenarioName(e.target.value)}
                  placeholder="Ex: Retard client principal"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optionnel)
                </label>
                <textarea
                  value={scenarioDescription}
                  onChange={(e) => setScenarioDescription(e.target.value)}
                  placeholder="Décrivez le contexte de ce scénario..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {impactTypes.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setImpactType(type.id)}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        impactType === type.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <Icon className={`h-6 w-6 ${
                          impactType === type.id ? 'text-blue-600' : 'text-gray-400'
                        }`} />
                        <div>
                          <div className="font-medium text-gray-900">{type.label}</div>
                          <div className="text-sm text-gray-500 mt-1">{type.description}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (impactType !== 'UNEXPECTED' && impactType !== 'SPREAD') && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Étape 2 : Sélectionner une ligne</h3>

              <div className="mb-4">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher une ligne..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="border rounded-lg max-h-96 overflow-y-auto">
                {filteredLines.map(line => (
                  <button
                    key={line.id}
                    onClick={() => setSelectedLineId(line.id)}
                    className={`w-full p-4 text-left border-b last:border-b-0 hover:bg-gray-50 transition-colors ${
                      selectedLineId === line.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{line.label}</div>
                        <div className="text-sm text-gray-500">
                          {new Date(line.date).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                      <div className={`font-semibold ${
                        line.amount >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {line.amount >= 0 ? '+' : ''}{line.amount.toLocaleString('fr-FR')} €
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (impactType === 'UNEXPECTED' || impactType === 'SPREAD') && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Étape 2 : Paramétrer l'impact</h3>
              <p className="text-sm text-gray-600 mb-4">
                Aucune ligne existante requise pour ce type d'impact.
              </p>
              <button
                onClick={handleNext}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Suivant
              </button>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Étape 3 : Paramétrer l'impact</h3>

              {selectedLine && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Ligne sélectionnée :</div>
                  <div className="font-medium text-gray-900">{selectedLine.label}</div>
                  <div className="text-sm text-gray-500">
                    {new Date(selectedLine.date).toLocaleDateString('fr-FR')} - {selectedLine.amount.toLocaleString('fr-FR')} €
                  </div>
                </div>
              )}

              {impactType === 'DELAY' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Délai de retard (jours)
                  </label>
                  <input
                    type="number"
                    value={delayDays}
                    onChange={(e) => setDelayDays(Number(e.target.value))}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    La ligne sera décalée de {delayDays} jours
                  </p>
                </div>
              )}

              {impactType === 'PARTIAL' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pourcentage encaissé (%)
                  </label>
                  <input
                    type="number"
                    value={percentage}
                    onChange={(e) => setPercentage(Number(e.target.value))}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Montant final : {selectedLine ? (selectedLine.amount * percentage / 100).toLocaleString('fr-FR') : 0} €
                  </p>
                </div>
              )}

              {impactType === 'LOSS' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">
                    Cette ligne sera complètement annulée (montant = 0 €)
                  </p>
                </div>
              )}

              {impactType === 'UNEXPECTED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant du décaissement (€)
                  </label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    min="0"
                    step="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Une dépense de {amount.toLocaleString('fr-FR')} € sera ajoutée aujourd'hui
                  </p>
                </div>
              )}

              {impactType === 'OVERCOST' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pourcentage d'augmentation (%)
                  </label>
                  <input
                    type="number"
                    value={percentage}
                    onChange={(e) => setPercentage(Number(e.target.value))}
                    min="0"
                    max="200"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Montant final : {selectedLine ? (selectedLine.amount * (1 + percentage / 100)).toLocaleString('fr-FR') : 0} €
                  </p>
                </div>
              )}

              {impactType === 'SPREAD' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Montant total (€)
                    </label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      step="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de mois
                    </label>
                    <input
                      type="number"
                      value={spreadPeriods}
                      onChange={(e) => setSpreadPeriods(Number(e.target.value))}
                      min="1"
                      max="24"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    {(amount / spreadPeriods).toLocaleString('fr-FR')} € par mois pendant {spreadPeriods} mois
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={() => {
              if (step > 1) setStep(step - 1);
              else onClose();
            }}
            className="px-4 py-2 text-gray-700 hover:text-gray-900"
          >
            {step === 1 ? 'Annuler' : 'Retour'}
          </button>
          <div className="flex items-center space-x-3">
            {step < 3 && (
              <button
                onClick={handleNext}
                disabled={
                  (step === 1 && !scenarioName) ||
                  (step === 2 && !selectedLineId && impactType !== 'UNEXPECTED' && impactType !== 'SPREAD')
                }
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            )}
            {step === 3 && (
              <button
                onClick={handleCreate}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Créer le scénario
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
